import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Pill, Reveal, WordTitle } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";
import ShaderBackground from "@/components/site/ShaderBackground";
import TalkFirst from "@/components/site/TalkFirst";
import AuditDoc, { docPages } from "@/components/site/AuditDoc";
import { Yours } from "@/components/site/AuditParts";
import { AUDIT, decode, encode, result } from "@/lib/check";
import { AUDIT_META, NOTES, SAMPLE_DOC } from "@/lib/sampleAudit";

export default function AuditSample() {
  const [params] = useSearchParams();
  const answers = decode(params.get("c"));
  const mine = answers ? result(answers) : null;
  // The one page a client's Audit does not have: the reader's own team.
  const extra = [
    {
      after: "scope",
      id: "yours",
      title: "The same leak at your size",
      note: NOTES.yours,
      // Sliders: nothing to print.
      screenOnly: true,
      body: <Yours mine={mine} price={SAMPLE_DOC.price.tool} />,
    },
  ];
  const count = docPages(SAMPLE_DOC, extra).length;

  const book = `/book${answers ? `?c=${encode(answers)}` : ""}`;
  return (
    <div data-testid="audit-sample">
      <section className="relative overflow-hidden bg-black px-5 pb-12 pt-28 text-white sm:pt-36 print:bg-white print:text-black">
        <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
        <div className="relative z-10 mx-auto max-w-[1400px]">
          <Pill white>Sample Audit · {AUDIT_META.number}</Pill>
          <WordTitle
            text="We audited our own outreach."
            className="mt-6 max-w-[1000px] text-[44px] leading-[0.98] sm:text-[80px] lg:text-[100px]"
          />
          <p className="mt-8 max-w-[640px] text-[19px] leading-[1.35] text-white/70 sm:text-[22px] print:text-black/70">
            After two weeks you hold two things: this document, and a first
            version of the tool that you have watched do one real case. This one
            describes a job of our own, so every number in it is ours to show.
            No client appears in it.
          </p>
          <dl className="mt-10 grid max-w-[900px] grid-cols-2 gap-x-8 gap-y-5 border-t border-white/15 pt-6 text-[14px] sm:grid-cols-4 print:border-black/15">
            {[
              ["The job", AUDIT_META.job],
              ["Run on", AUDIT_META.company],
              ["When", AUDIT_META.period],
              ["Prepared by", AUDIT_META.by],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-white/45 print:text-black/45">{k}</dt>
                <dd className="mt-1">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-10 flex flex-wrap items-center gap-3 print:hidden">
            <PillButton href="#answer" variant="inverse" size="lg">
              Read it
            </PillButton>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-11 items-center gap-2 rounded-full px-6 text-[13px] font-medium text-white ring-1 ring-white/30 transition-colors hover:bg-white/10"
            >
              <Printer size={14} /> Save as PDF
            </button>
            <span className="text-[13px] text-white/45">
              {count} pages · about twelve minutes
            </span>
          </div>
          {mine && (
            <p
              className="mt-10 max-w-[900px] rounded-[16px] bg-white/10 px-5 py-4 text-[15px] leading-[1.45] text-white/85 print:hidden"
              data-testid="sample-mine"
            >
              Your check: {mine.job.toLowerCase()}, {mine.hours[0]}–
              {mine.hours[1]} hours a month, stalling at{" "}
              {mine.leak.toLowerCase()}. Your Audit has the same pages, written
              about that job.
            </p>
          )}
        </div>
      </section>

      <AuditDoc doc={SAMPLE_DOC} extra={extra} />

      <section className="px-5 pt-16 lg:pt-24 print:hidden">
        <div className="mx-auto grid max-w-[1400px] items-end gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="nm-title text-[36px] leading-[1] sm:text-[64px]">
              Your job, written like this.
            </h2>
            <p className="mt-5 max-w-[560px] text-[18px] leading-[1.4] opacity-60 sm:text-[20px]">
              The Audit takes {AUDIT.time.toLowerCase()} and costs {AUDIT.price}
              . If the answer is no, you keep the document.
            </p>
          </Reveal>
          <div className="flex flex-wrap items-center gap-3 lg:col-span-5 lg:justify-end">
            <PillButton to={book} size="lg">
              Book the Audit
            </PillButton>
            <TalkFirst location="audit_sample" answers={answers} tone="light" />
            <Link to="/audit" className="nm-link w-fit text-[14px] opacity-70">
              What happens after you say yes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
